// src/pages/Contact.tsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Section from "../components/ui/Section";

const FORM_NAME = "contact";

type FormStatus = "idle" | "submitting" | "success" | "error";

const ErrorNotice = ({ onRetry }: { onRetry: () => void }) => (
  <div className="mt-4 border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-200">
    <div className="flex items-start gap-3">
      <svg
        className="h-5 w-5 flex-shrink-0 text-red-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <div>
        <p className="font-medium">Something went wrong</p>
        <p className="mt-1 opacity-90">
          We couldn't send your message. Please try again or email us directly at{" "}
          <a
            href="mailto:contact@nov-tia.com"
            className="underline"
          >
            contact@nov-tia.com
          </a>
        </p>
        <button
          onClick={onRetry}
          className="mt-2 text-xs font-medium underline"
        >
          Try again
        </button>
      </div>
    </div>
  </div>
);

const ContactPage: React.FC = () => {
  const [fields, setFields] = useState({
    name: "",
    email: "",
    company: "",
    topic: "",
    message: "",
    consent: false,
    "bot-field": "",
  });

  const [status, setStatus] = useState<FormStatus>("idle");

  const canSubmit = useMemo(() => {
    return (
      fields.name.trim().length > 0 &&
      fields.email.trim().length > 0 &&
      fields.message.trim().length > 0 &&
      fields.consent === true &&
      status !== "submitting"
    );
  }, [fields, status]);

  const resetForm = () => {
    setFields({
      name: "",
      email: "",
      company: "",
      topic: "",
      message: "",
      consent: false,
      "bot-field": "",
    });
    setStatus("idle");
  };

  const handleRetry = () => {
    setStatus("idle");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!canSubmit) return;

    setStatus("submitting");

    const formData = new URLSearchParams();
    formData.append("form-name", FORM_NAME);
    formData.append("name", fields.name);
    formData.append("email", fields.email);
    formData.append("company", fields.company);
    formData.append("topic", fields.topic);
    formData.append("message", fields.message);
    formData.append("consent", String(fields.consent));
    formData.append("bot-field", fields["bot-field"]);

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setStatus("success");
        setFields({
          name: "",
          email: "",
          company: "",
          topic: "",
          message: "",
          consent: false,
          "bot-field": "",
        });
      } else {
        console.error("Form submission failed:", response.status, response.statusText);
        setStatus("error");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus("error");
    }
  };

  const inputClasses =
    "w-full rounded-md border border-line-strong bg-surface/90 px-3 py-2.5 text-sm text-ink shadow-sm placeholder:text-muted focus:outline-none focus:border-signal focus:ring-2 focus:ring-signal/15 disabled:opacity-60 disabled:cursor-not-allowed transition-shadow";

  return (
    <div className="bg-bg">
      {/* Hero */}
      <section className="py-14 sm:py-16 border-b border-line">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="kicker mb-4">Contact</p>
            <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-ink mb-4">
              Let&apos;s talk about building AI you can actually trust.
            </h1>
            <p className="text-sm sm:text-base text-muted mb-6 max-w-xl">
              Whether you&apos;re exploring your first AI initiative, trying to
              bring governance and observability to existing systems, or need a
              partner for a specific product, NOVTIA can help.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 text-sm">
              <a
                href="#enquiry"
                className="inline-flex items-center bg-signal px-5 py-2 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors"
              >
                Send a message
              </a>
              <a
                href="mailto:contact@nov-tia.com?subject=NOVTIA%20Enquiry"
                className="inline-flex items-center border border-line-strong px-5 py-2 text-sm text-ink hover:border-signal hover:text-signal transition-colors"
              >
                Email instead
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <Section
        id="enquiry"
        eyebrow="Get in touch"
        title="A simple starting point for complex AI questions."
        description="Share a bit about your context and what you're trying to achieve. We use this to prepare before any call, so we don't waste your time with generic conversations."
      >
        <div className="grid lg:grid-cols-[1.1fr,1.1fr] gap-8 items-start">
          <div className="surface-card p-6 sm:p-7">
            {status === "success" ? (
              <div className="text-center py-8">
                <div className="mx-auto w-14 h-14 border border-emerald-500/40 flex items-center justify-center mb-4">
                  <svg
                    className="h-7 w-7 text-emerald-600 dark:text-emerald-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="font-display text-lg font-semibold text-ink mb-2">
                  Message sent!
                </h3>
                <p className="text-sm text-muted mb-6 max-w-sm mx-auto">
                  Thanks for reaching out. We'll review your message and get back to
                  you within 1-2 business days.
                </p>
                <button
                  onClick={resetForm}
                  className="inline-flex items-center border border-line-strong px-5 py-2 text-sm text-ink hover:border-signal hover:text-signal transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                name={FORM_NAME}
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="space-y-4 text-sm"
              >
                <input type="hidden" name="form-name" value={FORM_NAME} />

                <p className="hidden" aria-hidden="true">
                  <label>
                    Don't fill this out:
                    <input
                      name="bot-field"
                      value={fields["bot-field"]}
                      onChange={(e) =>
                        setFields((s) => ({ ...s, "bot-field": e.target.value }))
                      }
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </label>
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-xs font-medium text-muted mb-1"
                    >
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      required
                      value={fields.name}
                      onChange={(e) =>
                        setFields((s) => ({ ...s, name: e.target.value }))
                      }
                      disabled={status === "submitting"}
                      className={inputClasses}
                      placeholder="Your name"
                      autoComplete="name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs font-medium text-muted mb-1"
                    >
                      Work email <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={fields.email}
                      onChange={(e) =>
                        setFields((s) => ({ ...s, email: e.target.value }))
                      }
                      disabled={status === "submitting"}
                      className={inputClasses}
                      placeholder="you@yourcompany.com"
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="company"
                    className="block text-xs font-medium text-muted mb-1"
                  >
                    Company or organisation
                  </label>
                  <input
                    id="company"
                    name="company"
                    value={fields.company}
                    onChange={(e) =>
                      setFields((s) => ({ ...s, company: e.target.value }))
                    }
                    disabled={status === "submitting"}
                    className={inputClasses}
                    placeholder="Organisation name"
                    autoComplete="organization"
                  />
                </div>

                <div>
                  <label
                    htmlFor="topic"
                    className="block text-xs font-medium text-muted mb-1"
                  >
                    What do you want to discuss?
                  </label>
                  <select
                    id="topic"
                    name="topic"
                    value={fields.topic}
                    onChange={(e) =>
                      setFields((s) => ({ ...s, topic: e.target.value }))
                    }
                    disabled={status === "submitting"}
                    className={inputClasses}
                  >
                    <option value="">Select a topic</option>
                    <option value="strategy">AI strategy & opportunity mapping</option>
                    <option value="governance">Governance, safety, or policy for AI</option>
                    <option value="airg">AIRG pilot or product enquiry</option>
                    <option value="observability">Observability & monitoring for AI systems</option>
                    <option value="product">End-to-end AI product or PoC</option>
                    <option value="labs">NOVTIA Labs collaboration</option>
                    <option value="other">Something else</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-xs font-medium text-muted mb-1"
                  >
                    Briefly describe your context{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={fields.message}
                    onChange={(e) =>
                      setFields((s) => ({ ...s, message: e.target.value }))
                    }
                    disabled={status === "submitting"}
                    className={`${inputClasses} resize-none`}
                    placeholder="For example: current systems, challenges, timelines, or what success looks like for you."
                  />
                </div>

                <div className="flex items-start gap-2 text-xs text-muted">
                  <input
                    id="consent"
                    name="consent"
                    type="checkbox"
                    checked={fields.consent}
                    onChange={(e) =>
                      setFields((s) => ({ ...s, consent: e.target.checked }))
                    }
                    disabled={status === "submitting"}
                    className="mt-0.5 h-3.5 w-3.5 accent-signal disabled:opacity-60"
                    required
                  />
                  <label htmlFor="consent">
                    I&apos;m happy for NOVTIA to contact me about this enquiry and
                    understand this isn&apos;t a mailing list signup.
                  </label>
                </div>

                <div className="pt-2 flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="inline-flex items-center justify-center gap-2 bg-signal px-5 py-2 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors disabled:opacity-60 disabled:cursor-not-allowed min-w-[140px]"
                  >
                    {status === "submitting" ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      "Send message"
                    )}
                  </button>

                  <p className="text-xs text-muted">
                    Prefer email?{" "}
                    <a
                      className="text-ink hover:text-signal hover:underline"
                      href="mailto:contact@nov-tia.com?subject=NOVTIA%20Enquiry"
                    >
                      contact@nov-tia.com
                    </a>
                  </p>
                </div>

                {status === "error" && <ErrorNotice onRetry={handleRetry} />}
              </form>
            )}
          </div>

          {/* Right side content */}
          <div className="space-y-5">
            <div className="surface-card p-5">
              <h3 className="font-display text-base font-semibold text-ink mb-3">
                What happens next?
              </h3>
              <ol className="space-y-3 text-sm text-muted">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 border border-signal/40 text-signal flex items-center justify-center text-xs font-mono">
                    1
                  </span>
                  <span>
                    We review your message and research your context (usually within
                    24 hours)
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 border border-signal/40 text-signal flex items-center justify-center text-xs font-mono">
                    2
                  </span>
                  <span>
                    We reply with initial thoughts and suggest a call if it makes
                    sense
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 border border-signal/40 text-signal flex items-center justify-center text-xs font-mono">
                    3
                  </span>
                  <span>
                    No pressure — if we're not the right fit, we'll tell you
                    honestly
                  </span>
                </li>
              </ol>
            </div>

            <div className="surface-card p-5">
              <h3 className="font-display text-base font-semibold text-ink mb-3">
                Other ways to connect
              </h3>
              <div className="space-y-3">
                <a
                  href="mailto:contact@nov-tia.com"
                  className="flex items-center gap-3 text-sm text-ink hover:text-signal transition-colors"
                >
                  <svg
                    className="w-5 h-5 text-muted"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  contact@nov-tia.com
                </a>
                <a
                  href="https://www.linkedin.com/company/novtia/about/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-sm text-ink hover:text-signal transition-colors"
                >
                  <svg
                    className="w-5 h-5 text-muted"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default ContactPage;
